import React, { FC, useEffect } from "react";

import { Category, DataPoint } from "fileParsing/types";
import { useDatapointsFromExportId } from "containers/hooks/useData";
import AnimatedPieChart, { PieChartData } from "./AnimatedPieChart";
import { Sensitivity, SensitivityLevel } from "./sensitivity";

export type VisualizationSettings = {
  width: number;
  height: number;
  radiusScale: number;
  radiusScaleLabels: number;
  colors: {
    [SensitivityLevel.Low]: string;
    [SensitivityLevel.Medium]: string;
    [SensitivityLevel.High]: string;
  };
  icons: {
    [SensitivityLevel.Low]: string;
    [SensitivityLevel.Medium]: string;
    [SensitivityLevel.High]: string;
  };
  spacing: number;
  borderWidth: number;
  subcategoryAggregation: string[];
};

export const VisualizationSettingsDefaults: VisualizationSettings = {
  width: 900,
  height: 300,
  radiusScale: 0.75,
  radiusScaleLabels: 0.8,
  colors: {
    [SensitivityLevel.Low]: "#39bbee",
    [SensitivityLevel.Medium]: "#e8a21c",
    [SensitivityLevel.High]: "#e83333",
  },
  icons: {
    [SensitivityLevel.Low]:
      "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z",
    [SensitivityLevel.Medium]:
      "M11.99,2C6.47,2 2,6.48 2,12C2,17.52 6.47,22 11.99,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 11.99,2ZM12,20C7.58,20 4,16.42 4,12C4,7.58 7.58,4 12,4C16.42,4 20,7.58 20,12C20,16.42 16.42,20 12,20ZM16.564,14.32L7.436,14.32L7.436,15.68L16.564,15.68L16.564,14.32ZM15.5,11C16.33,11 17,10.33 17,9.5C17,8.67 16.33,8 15.5,8C14.67,8 14,8.67 14,9.5C14,10.33 14.67,11 15.5,11ZM8.5,11C9.33,11 10,10.33 10,9.5C10,8.67 9.33,8 8.5,8C7.67,8 7,8.67 7,9.5C7,10.33 7.67,11 8.5,11Z",
    [SensitivityLevel.High]:
      "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 3c-2.33 0-4.31 1.46-5.11 3.5h10.22c-.8-2.04-2.78-3.5-5.11-3.5z",
  },
  spacing: 20,
  borderWidth: 4,
  subcategoryAggregation: ["Chat with", "Album", "Chat with"],
};

export type SubCategories = {
  [key: string]: {
    category: Category;
    sensitivity: SensitivityLevel | null;
    count: number;
  };
};

// Component-specific props.
type ComponentProps = {
  exportId?: number;
  subCategories: SubCategories;
  settings: Partial<VisualizationSettings>;
};

const PieChart: FC<ComponentProps> = (props: ComponentProps) => {
  const chart = React.useRef<SVGGElement>(null);
  const chartLabels = React.useRef<SVGGElement>(null);
  const settings = getSettings();
  const height = settings.height;

  // get array of all data points
  const dataPoints = useDatapointsFromExportId(props.exportId);
  const totalCount = dataPoints.length;

  // collect all subcategories
  const [subCategories, setSubCategories] = React.useState<SubCategories>(getSubCategories());
  const [pieData, setPieData] = React.useState<PieChartData[]>(getPieData());

  useEffect(() => {
    setSubCategories(getSubCategories());
  }, [chart, chartLabels, dataPoints.length, totalCount, JSON.stringify(props.subCategories)]);

  useEffect(() => {
    setPieData(getPieData);
  }, [JSON.stringify(subCategories)]);

  function getSubCategories(): SubCategories {
    const temp = dataPoints.reduce<SubCategories>((acc, d: DataPoint) => {
      let subcat = d.subcategory;

      // prevent multiple, similar subcategories as specified in settings
      const match = settings.subcategoryAggregation.find((s) => d.subcategory.startsWith(s));
      if (match !== undefined) {
        subcat = match;
      }

      return {
        ...acc,
        [subcat]: {
          category: d.category,
          sensitivity:
            props.subCategories && props.subCategories[subcat] ? props.subCategories[subcat].sensitivity : null,
          count: acc[subcat] ? acc[subcat].count + 1 : 1,
        },
      };
    }, {} as SubCategories);

    // get initial sensitivity values for each given subcategory
    const sensitivities = Sensitivity.getSensitivity(temp);

    // add sensitivity values to subCategories
    Object.entries(sensitivities).forEach(([subcat, sensitivity]) => {
      if (!temp[subcat].sensitivity) {
        temp[subcat].sensitivity = sensitivity;
      }
    });

    return temp;
  }

  function getPieData() {
    return Object.values(SensitivityLevel).map((level) => {
      const matches = Object.entries(subCategories).filter((sc) => sc[1].sensitivity === level);
      return {
        key: level,
        subCategories: matches.map((m) => m[0]),
        value: matches.reduce((acc, sc) => acc + sc[1].count, 0),
        color: settings.colors[level],
      };
    });
  }

  function getSettings(): VisualizationSettings {
    return {
      ...VisualizationSettingsDefaults,
      ...props.settings,
    };
  }

  return (
    <>
      <AnimatedPieChart data={pieData} size={{ width: "100%", height }} totalCount={totalCount} />
    </>
  );
};

export default PieChart;
